import React, { useEffect, useState, useRef } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { getUsers, updateProfile, uploadProfileImage } from '../api';
import api from '../api';

const USER_TYPES = ['Employee', 'Contractor', 'Client', 'Admin', 'Freelancer', 'Intern'];

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Anchorage', 'Pacific/Honolulu', 'Europe/London', 'Europe/Paris',
  'Europe/Berlin', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore',
  'Asia/Tokyo', 'Australia/Sydney', 'Pacific/Auckland',
];

export default function ProfilePage() {
  const { user: authUser } = useAuth();
  const [profile,   setProfile]   = useState(null);
  const [form,      setForm]       = useState({});
  const [password,  setPassword]   = useState('');
  const [confirm,   setConfirm]    = useState('');
  const [saving,    setSaving]     = useState(false);
  const [msg,       setMsg]        = useState('');
  const [imgLoading,setImgLoading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    // Fetch current user's full profile
    api.get('/wp/v2/users/me?context=edit')
      .then(res => {
        setProfile(res.data);
        setForm({
          first_name:       res.data.first_name || '',
          last_name:        res.data.last_name  || '',
          display_name:     res.data.name       || '',
          email:            res.data.email      || '',
          gpf_job_title:    res.data.gpf_job_title   || '',
          gpf_phone:        res.data.gpf_phone        || '',
          gpf_address:      res.data.gpf_address      || '',
          gpf_user_type:    res.data.gpf_user_type    || '',
          gpf_timezone:     res.data.gpf_timezone     || '',
          gpf_profile_image:res.data.gpf_profile_image|| '',
        });
      })
      .catch(console.error);
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (password && password !== confirm) {
      setMsg('Passwords do not match.'); return;
    }
    setSaving(true);
    try {
      const data = { ...form };
      if (password) data.password = password;
      await updateProfile(data);
      // Update localStorage display name
      const stored = JSON.parse(localStorage.getItem('gpf_user') || '{}');
      stored.name = form.display_name;
      localStorage.setItem('gpf_user', JSON.stringify(stored));
      setMsg('Profile saved successfully!');
      setPassword(''); setConfirm('');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg('Failed to save profile.');
    } finally { setSaving(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImgLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await uploadProfileImage(formData);
      setForm(prev => ({ ...prev, gpf_profile_image: res.data.image_url }));
      // Update localStorage
      const stored = JSON.parse(localStorage.getItem('gpf_user') || '{}');
      stored.avatar = res.data.image_url;
      localStorage.setItem('gpf_user', JSON.stringify(stored));
    } catch (err) {
      setMsg('Failed to upload image.');
    } finally { setImgLoading(false); }
  };

  const initials = (name) => (name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  if (!profile) return <Layout><p style={{ color: '#64748b', padding: 32 }}>Loading profile...</p></Layout>;

  return (
    <Layout>
      <div style={styles.page}>
        <div style={styles.header}>
          <h1 style={styles.title}>My Profile</h1>
          <p style={styles.subtitle}>Manage your personal information and preferences</p>
        </div>

        <form onSubmit={handleSave} style={styles.layout}>

          {/* Left: Avatar + basic */}
          <div style={styles.sidebar}>
            <div style={styles.avatarCard}>
              <div style={styles.avatarWrap}>
                {form.gpf_profile_image ? (
                  <img src={form.gpf_profile_image} alt="Profile" style={styles.avatarImg} />
                ) : (
                  <div style={styles.avatarFallback}>{initials(form.display_name)}</div>
                )}
                <button type="button" style={styles.changeAvatarBtn} onClick={() => fileRef.current?.click()} disabled={imgLoading}>
                  {imgLoading ? '⏳' : '📷'}
                </button>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
              </div>
              <h2 style={styles.avatarName}>{form.display_name || 'Your Name'}</h2>
              <p style={styles.avatarRole}>{form.gpf_job_title || 'No title set'}</p>
              {form.gpf_user_type && <span style={styles.userTypeBadge}>{form.gpf_user_type}</span>}
            </div>
          </div>

          {/* Right: Form */}
          <div style={styles.formArea}>

            {/* Personal info */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Personal Information</h3>
              <div style={styles.grid}>
                <div style={styles.field}>
                  <label style={styles.label}>First Name</label>
                  <input style={styles.input} value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Last Name</label>
                  <input style={styles.input} value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} />
                </div>
                <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Display Name</label>
                  <input style={styles.input} value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} />
                </div>
                <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Email</label>
                  <input style={styles.input} type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Job Title</label>
                  <input style={styles.input} value={form.gpf_job_title} onChange={e => setForm({ ...form, gpf_job_title: e.target.value })} placeholder="e.g. Product Manager" />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>User Type</label>
                  <select style={styles.input} value={form.gpf_user_type} onChange={e => setForm({ ...form, gpf_user_type: e.target.value })}>
                    <option value="">Select type...</option>
                    {USER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Phone</label>
                  <input style={styles.input} value={form.gpf_phone} onChange={e => setForm({ ...form, gpf_phone: e.target.value })} placeholder="+1 (555) 000-0000" />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Timezone</label>
                  <select style={styles.input} value={form.gpf_timezone} onChange={e => setForm({ ...form, gpf_timezone: e.target.value })}>
                    <option value="">Select timezone...</option>
                    {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz.replace('_', ' ')}</option>)}
                  </select>
                </div>
                <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Address</label>
                  <textarea style={{ ...styles.input, height: 70, resize: 'vertical' }} value={form.gpf_address} onChange={e => setForm({ ...form, gpf_address: e.target.value })} placeholder="Street, City, State, ZIP" />
                </div>
              </div>
            </div>

            {/* Password */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Change Password</h3>
              <div style={styles.grid}>
                <div style={styles.field}>
                  <label style={styles.label}>New Password</label>
                  <input style={styles.input} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Leave blank to keep current" />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Confirm Password</label>
                  <input style={styles.input} type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat new password" />
                </div>
              </div>
            </div>

            {msg && <p style={{ ...styles.msg, color: msg.includes('success') ? '#16a34a' : '#dc2626' }}>{msg}</p>}

            <div style={styles.actions}>
              <button type="submit" style={styles.primaryBtn} disabled={saving}>
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
}

const styles = {
  page:          { maxWidth: 900 },
  header:        { marginBottom: 28 },
  title:         { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  subtitle:      { color: '#64748b', fontSize: 14, margin: '4px 0 0' },
  layout:        { display: 'flex', gap: 24, alignItems: 'flex-start' },
  sidebar:       { width: 220, flexShrink: 0 },
  avatarCard:    { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', textAlign: 'center' },
  avatarWrap:    { position: 'relative', display: 'inline-block', marginBottom: 12 },
  avatarImg:     { width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid #e2e8f0' },
  avatarFallback:{ width: 80, height: 80, borderRadius: '50%', background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700, border: '3px solid #e2e8f0' },
  changeAvatarBtn:{ position: 'absolute', bottom: 0, right: 0, width: 26, height: 26, borderRadius: '50%', background: '#fff', border: '2px solid #e2e8f0', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  avatarName:    { fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '0 0 4px' },
  avatarRole:    { fontSize: 12, color: '#64748b', margin: '0 0 8px' },
  userTypeBadge: { display: 'inline-block', fontSize: 11, fontWeight: 700, color: '#2563eb', background: '#eff6ff', padding: '2px 10px', borderRadius: 99 },
  formArea:      { flex: 1, display: 'flex', flexDirection: 'column', gap: 16 },
  card:          { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  cardTitle:     { fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '0 0 18px' },
  grid:          { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  field:         { display: 'flex', flexDirection: 'column', gap: 6 },
  label:         { fontSize: 13, fontWeight: 600, color: '#374151' },
  input:         { padding: '9px 12px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 14, outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  msg:           { fontSize: 13, fontWeight: 500, margin: 0 },
  actions:       { display: 'flex', justifyContent: 'flex-end' },
  primaryBtn:    { padding: '10px 24px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};